<template>
  <div class="assistant-row" :class="{ 'mt-3': showAvatar, 'mt-1': !showAvatar }">
    <!-- Avatar -->
    <div class="avatar-col" v-if="showAvatar">
      <div class="felix-avatar-wrap">
        <v-img
          src="../../assets/felix/icon/Icone_SemFundo.svg"
          width="28"
          height="28"
          contain
        ></v-img>
      </div>
    </div>

    <!-- Message bubble -->
    <div class="bubble-col" :class="{ 'bubble-col--no-avatar': !showAvatar }">
      <div
        class="assistant-bubble"
        :class="{
          'assistant-bubble--error': message.isError,
          'assistant-bubble--streaming': isStreaming,
        }"
      >
        <div class="message-content" v-html="formattedContent"></div>
        <span v-if="isStreaming && message.content" class="streaming-cursor"></span>

        <!-- Actions row - visible on hover or if has info -->
        <!-- Footer: time + actions (always visible) -->
        <div class="actions-row mt-2 d-flex align-center">
          <span class="time-text">
            {{ formatTime(message.createdAt) }}
          </span>
          <span v-if="message.processingTimeMs" class="time-text ml-1">
            · {{ formatProcessingTime(message.processingTimeMs) }}
          </span>

          <v-spacer></v-spacer>

          <span
            v-if="message.isError"
            class="action-icon"
            @click="$emit('retry', message.originalMessage)"
            title="Tentar novamente"
          >
            <v-icon size="15" color="#FFB74D">mdi-refresh</v-icon>
          </span>

          <span
            v-if="hasAdditionalInfo"
            class="action-icon"
            @click="showDetails = !showDetails"
            title="Detalhes"
          >
            <v-icon size="15" color="rgba(255,255,255,0.7)">{{ showDetails ? 'mdi-chevron-up' : 'mdi-information-outline' }}</v-icon>
          </span>

          <span
            class="action-icon"
            @click="copyToClipboard"
            title="Copiar"
          >
            <v-icon size="15" color="rgba(255,255,255,0.7)">mdi-content-copy</v-icon>
          </span>
        </div>

        <!-- Details panel (expandable) -->
        <v-expand-transition>
          <div v-if="showDetails && hasAdditionalInfo" class="details-panel mt-2">
            <div v-if="message.context" class="mb-2">
              <div class="caption font-weight-bold mb-1">Contexto:</div>
              <div class="detail-box caption">{{ message.context }}</div>
            </div>
            <div v-if="message.queriedData" class="mb-2">
              <div class="caption font-weight-bold mb-1">Dados consultados:</div>
              <div class="detail-box caption">{{ message.queriedData }}</div>
            </div>
            <div class="d-flex flex-wrap caption" style="gap: 8px; opacity: 0.7;">
              <span v-if="message.inputTokens">In: {{ message.inputTokens }}</span>
              <span v-if="message.outputTokens">Out: {{ message.outputTokens }}</span>
              <span v-if="message.tokensUsed && !message.inputTokens">Tokens: {{ message.tokensUsed }}</span>
              <span v-if="message.cost">Custo: ${{ message.cost.toFixed(6) }}</span>
              <span v-if="message.model">{{ message.model }}</span>
            </div>
          </div>
        </v-expand-transition>
      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment'

export default {
  name: 'AIAssistantMessage',

  props: {
    message: {
      type: Object,
      required: true,
      default: () => ({
        content: '',
        createdAt: '',
        role: 'Assistant',
        processingTimeMs: null,
        tokensUsed: null,
        cost: null,
        context: null,
        queriedData: null,
        model: null,
      }),
    },
    felixVideos: {
      type: Object,
      default: () => ({}),
    },
    felixStateKey: {
      type: String,
      default: 'neutral',
    },
    isStreaming: {
      type: Boolean,
      default: false,
    },
    showAvatar: {
      type: Boolean,
      default: true,
    },
  },

  data() {
    return {
      showDetails: false,
    }
  },

  computed: {
    formattedContent() {
      if (!this.message.content) return ''

      let content = this.message.content

      // Process code blocks first (```)
      content = content.replace(/```([\s\S]*?)```/g, '<pre><code>$1</code></pre>')

      // Then inline formatting
      content = content
        .replace(/\n/g, '<br>')
        .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
        .replace(/(?<!\*)\*(?!\*)(.+?)(?<!\*)\*(?!\*)/g, '<em>$1</em>')
        .replace(/`(.*?)`/g, '<code class="inline-code">$1</code>')

      // Headers (on their own line)
      content = content.replace(/<br>### (.*?)(<br>|$)/g, '<h4>$1</h4>')
      content = content.replace(/<br>## (.*?)(<br>|$)/g, '<h3>$1</h3>')
      content = content.replace(/<br># (.*?)(<br>|$)/g, '<h2>$1</h2>')

      // Lists - numbered
      content = content.replace(/(?:<br>)?\d+\.\s(.*?)(?=<br>\d+\.|<br>[^<]|$)/g, '<li>$1</li>')

      // Lists - bulleted
      content = content.replace(/(?:<br>)?[-•]\s(.*?)(?=<br>[-•]|<br>[^<]|$)/g, '<li>$1</li>')

      // Wrap consecutive <li> in <ul>
      content = content.replace(/((?:<li>.*?<\/li>)+)/g, '<ul>$1</ul>')

      return content
    },

    hasAdditionalInfo() {
      return (
        this.message.context ||
        this.message.queriedData ||
        this.message.tokensUsed ||
        this.message.inputTokens ||
        this.message.outputTokens ||
        this.message.cost ||
        this.message.model
      )
    },
  },

  methods: {
    formatTime(dateTime) {
      if (!dateTime) return ''
      const now = moment()
      const messageTime = moment(dateTime)
      if (now.diff(messageTime, 'minutes') < 1) return 'Agora'
      if (now.diff(messageTime, 'hours') < 1) return `${now.diff(messageTime, 'minutes')} min`
      if (now.diff(messageTime, 'days') < 1) return messageTime.format('HH:mm')
      return messageTime.format('DD/MM HH:mm')
    },

    formatProcessingTime(ms) {
      if (!ms) return ''
      if (ms < 1000) return `${ms}ms`
      return `${(ms / 1000).toFixed(1)}s`
    },

    async copyToClipboard() {
      try {
        await navigator.clipboard.writeText(this.message.content)
        this.$toast.success('Copiado!')
      } catch (error) {
        this.$toast.error('Erro ao copiar')
      }
    },
  },
}
</script>

<style scoped>
.assistant-row {
  display: flex;
  align-items: flex-start;
  max-width: 88%;
  animation: fadeSlideIn 0.25s ease-out;
}

.avatar-col {
  flex-shrink: 0;
  margin-right: 8px;
  margin-top: 2px;
}

.felix-avatar-wrap {
  width: 28px;
  height: 28px;
  border-radius: 8px;
  background: linear-gradient(135deg, #e8f5e9, #c8e6c9);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 3px;
}

.bubble-col {
  flex: 1;
  min-width: 0;
}

.bubble-col--no-avatar {
  margin-left: 36px;
}

.assistant-bubble {
  background: #0F3565;
  color: #fff;
  border-radius: 4px 16px 16px 16px;
  padding: 10px 14px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.08);
  position: relative;
  transition: box-shadow 0.2s ease;
}

.assistant-bubble:hover {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
}

.assistant-bubble--error {
  background: #1a3d6d;
  border-left: 3px solid #ff9800;
}

.assistant-bubble--streaming {
  border-left: 2px solid #B9CE1E;
}

/* ===== Message Content ===== */
.message-content {
  font-family: 'Poppins', sans-serif;
  font-size: 13px;
  line-height: 1.6;
  color: #fff;
  word-wrap: break-word;
}

.message-content ::v-deep strong {
  font-weight: 600;
  color: #fff;
}

.message-content ::v-deep em {
  font-style: italic;
}

.message-content ::v-deep .inline-code {
  background: rgba(255, 255, 255, 0.15);
  padding: 1px 5px;
  border-radius: 3px;
  font-family: 'JetBrains Mono', 'Fira Code', monospace;
  font-size: 12px;
  color: #B9CE1E;
}

.message-content ::v-deep pre {
  background: #1e1e1e;
  color: #d4d4d4;
  padding: 10px 12px;
  border-radius: 8px;
  overflow-x: auto;
  margin: 8px 0;
  font-size: 12px;
}

.message-content ::v-deep pre code {
  background: none;
  color: inherit;
  padding: 0;
  font-family: 'JetBrains Mono', 'Fira Code', monospace;
}

.message-content ::v-deep h2,
.message-content ::v-deep h3,
.message-content ::v-deep h4 {
  margin: 10px 0 6px 0;
  font-weight: 600;
  color: #fff;
}

.message-content ::v-deep h2 { font-size: 15px; }
.message-content ::v-deep h3 { font-size: 14px; }
.message-content ::v-deep h4 { font-size: 13px; }

.message-content ::v-deep ul {
  padding-left: 18px;
  margin: 6px 0;
}

.message-content ::v-deep li {
  margin: 3px 0;
}

/* ===== Streaming Cursor ===== */
.streaming-cursor {
  display: inline-block;
  width: 2px;
  height: 14px;
  background: #B9CE1E;
  margin-left: 2px;
  vertical-align: text-bottom;
  animation: cursorBlink 0.8s step-end infinite;
}

@keyframes cursorBlink {
  0%, 100% { opacity: 1; }
  50% { opacity: 0; }
}

/* ===== Actions ===== */
.actions-row {
  min-height: 22px;
}

.time-text {
  font-size: 10px !important;
  user-select: none;
  color: rgba(255, 255, 255, 0.5);
}

.action-icon {
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 4px;
  transition: background 0.15s ease;
  margin-left: 2px;
}

.action-icon:hover {
  background: rgba(255, 255, 255, 0.15);
}

.action-icon:hover .v-icon {
  color: #fff !important;
}

/* ===== Details Panel ===== */
.details-panel {
  background: rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  padding: 10px;
  font-size: 11px;
  color: rgba(255, 255, 255, 0.8);
}

.details-panel .caption {
  color: rgba(255, 255, 255, 0.8) !important;
}

.detail-box {
  background: rgba(255, 255, 255, 0.08);
  padding: 6px 8px;
  border-radius: 4px;
  font-family: monospace;
  font-size: 11px;
  max-height: 80px;
  overflow-y: auto;
  border: 1px solid rgba(255, 255, 255, 0.1);
  color: rgba(255, 255, 255, 0.9);
}

/* ===== Animation ===== */
@keyframes fadeSlideIn {
  from {
    opacity: 0;
    transform: translateY(6px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
